"use client";

import translations from "@/lib/translations.json";
import { ClipboardCheck } from "lucide-react";
import { useRef } from "react";
import { useWebsiteLang } from "../context/website-lang-context";
import { ReportWindow } from "./reports-showcase/ReportWindow";

// Reports as a selling point for the agency: its clients get a report of every shift. The
// window shows the real report components with sample data (see reports-showcase/).

export default function ReportsShowcase() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.reportsShowcase;
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section
      id="reports"
      ref={sectionRef}
      className="relative overflow-hidden py-20 sm:py-28"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900/50 to-navy-950" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:gap-16">
          <div className="min-w-0 space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-400 glass">
                <ClipboardCheck size={14} />
                {t.badge}
              </div>
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                {t.titlePrefix}{" "}
                <span className="text-gradient-amber">{t.titleHighlight}</span>
              </h2>
              <p className="text-base leading-relaxed text-white/60 md:text-lg">
                {t.description}
              </p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-navy-900/80">
              <table className="w-full border-collapse text-left text-sm sm:text-base">
                <thead>
                  <tr className="bg-white/5 text-[11px] font-medium uppercase tracking-wider">
                    <th scope="col" className="px-3 py-3 text-white/50 sm:px-4">
                      {t.table.headers[0]}
                    </th>
                    <th scope="col" className="px-3 py-3 text-white/50 sm:px-4">
                      {t.table.headers[1]}
                    </th>
                    <th scope="col" className="px-3 py-3 text-amber-400 sm:px-4">
                      {t.table.headers[2]}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {t.table.rows.map((row) => (
                    <tr key={row[0]} className="border-t border-white/10">
                      <th scope="row" className="px-3 py-3.5 font-medium text-white sm:px-4">
                        {row[0]}
                      </th>
                      <td className="px-3 py-3.5 text-white/50 sm:px-4">{row[1]}</td>
                      <td className="px-3 py-3.5 font-semibold text-teal-400 sm:px-4">
                        {row[2]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <ReportWindow
            trackRef={sectionRef}
            label={t.window.label}
            note={t.window.note}
          />
        </div>
      </div>
    </section>
  );
}
